/**
 * Types pour l'échange de données comptables (import/export)
 * Conforme au backend data-exchange controller v1
 */

// ====== ENUMS ======

/** Format technique du fichier */
export type FileFormat = 'csv' | 'txt' | 'xlsx' | 'xls' | 'xml' | 'json' | 'pdf';

/** Standard comptable du contenu */
export type AccountingStandard = 'fec' | 'saf_t' | 'xbrl' | 'syscohada' | 'ohada_revise' | 'generic' | 'pennylane' | 'csv_standard';

/** Type de données à importer/exporter */
export type DataType = 
  | 'fiscal_year' 
  | 'journal_entries' 
  | 'chart_of_accounts' 
  | 'trial_balance' 
  | 'general_ledger' 
  | 'invoices' 
  | 'third_parties';

/** Encodage du fichier */
export type FileEncoding = 'utf-8' | 'utf-8-bom' | 'latin1' | 'iso-8859-1' | 'iso-8859-15' | 'windows-1252';

/** Séparateur de champs */
export type FieldSeparator = '|' | ';' | ',' | '\t';

/** Statut d'une opération d'import */
export type OperationStatus =
  | 'pending'
  | 'downloading'
  | 'processing'
  | 'parsing'
  | 'validating'
  | 'importing'
  | 'completed'
  | 'failed'
  | 'partial'
  | 'cancelled';

/** Mode de validation */
export type ValidationMode = 'strict' | 'lenient';

/** Framework comptable */
export type AccountingFramework = 'SYSCOHADA' | 'IFRS';

// ====== FORMAT SUPPORTÉ ======

export interface SupportedFormat {
  format: string;
  name: string;
  description: string;
  extensions: string[];
  mimeTypes: string[];
  features: string[];
}

// ====== DÉTECTION DE FORMAT ======

export interface DetectedFormat {
  format: string;
  confidence: number;
  details: string;
  canParse: boolean;
}

export interface FormatDetectionResult {
  success: boolean;
  detectedFormats: DetectedFormat[];
  recommendedFormat: string;
  confidence: number;
}

// ====== PRÉVISUALISATION ======

export interface PreviewStatistics {
  totalLines: number;
  /** Alias renvoyé par le backend */
  parsedLines?: number;
  dateRange: { start: string; end: string } | null;
  journalCodes: string[];
  /** Objet { codeJournal: count } renvoyé par le backend */
  journalCounts?: Record<string, number>;
  /** Objet { classeCompte: count } renvoyé par le backend */
  accountCounts?: Record<string, number>;
  totalDebit: number;
  totalCredit: number;
  isBalanced: boolean;
}

export interface PreviewResult {
  success: boolean;
  detectedFormat: string;
  columnHeaders: string[];
  preview: PreviewRow[];
  statistics: PreviewStatistics;
  totalPreviewLines: number;
}

/** Une ligne de prévisualisation renvoyée par le backend */
export interface PreviewRow {
  journalCode?: string;
  journalName?: string;
  entryNumber?: string;
  entryDate?: string;
  accountCode?: string;
  accountName?: string;
  reference?: string;
  documentDate?: string;
  description?: string;
  debit?: number;
  credit?: number;
  /** Le backend peut renvoyer un tableau OU un objet { headerName: value } */
  originalData?: Record<string, string | number | null> | (string | number | null)[];
  lineNumber?: number;
  [key: string]: unknown;
}

// ====== CONFIGURATION D'IMPORT ======

export interface ImportConfig {
  fiscalYearId?: string;
  fileFormat?: FileFormat;
  accountingStandard?: AccountingStandard;
  dataType?: DataType;
  encoding?: FileEncoding;
  separator?: FieldSeparator;
  hasHeader?: boolean;
  columnMapping?: Record<string, string>;
  createFiscalYearIfNotExists?: boolean;
  createMissingAccounts?: boolean;
  validationMode?: ValidationMode;
  continueOnError?: boolean;
  maxErrors?: number;
  dateFormat?: string;
  numberFormat?: 'european' | 'us';
  accountingFramework?: AccountingFramework;
}

// ====== IMPORT DEPUIS URL (ASYNCHRONE) ======

export interface ImportFromUrlRequest {
  fileUrl: string;
  fiscalYearId: string;
  fileFormat?: FileFormat;
  accountingStandard?: AccountingStandard;
  config?: ImportConfig;
}

export interface ImportFromUrlResponse {
  taskId: string;
  status: string;
  message: string;
  websocketUrl?: string;
  websocketEvent?: string;
}

// ====== IMPORT UPLOAD (SYNCHRONE) ======

export interface ImportUploadResponse {
  success: boolean;
  operationId: string;
  message: string;
  importedCount: number;
  skippedCount: number;
  errorCount: number;
  fiscalYearId: string;
  errors: ImportError[];
  warnings: string[];
  duration: number;
}

export interface ImportError {
  line: number;
  message: string;
  code: string;
  field?: string;
}

// ====== STATUT DE TÂCHE ======

export interface ImportTaskStatus {
  taskId: string;
  status: OperationStatus;
  progress: number;
  currentStep: string;
  processedLines: number;
  totalLines: number;
  importedLines: number;
  errorCount: number;
  createdAt: string;
  completedAt: string | null;
}

// ====== RÉSULTAT WEBSOCKET ======

export interface ImportTaskResult {
  taskId: string;
  status: OperationStatus;
  progress: number;
  result?: {
    success: boolean;
    importedCount: number;
    errorCount: number;
    skippedCount: number;
    duration: number;
    fiscalYearId: string;
    journalsCreated?: number;
  };
  error?: {
    code: string;
    message: string;
    details?: Record<string, any>;
  };
  estimatedTimeRemaining?: number;
}

// ====== VALIDATION ======

export interface ValidationResult {
  success: boolean;
  isValid: boolean;
  errorCount: number;
  warningCount: number;
  errors: ImportError[];
  warnings: string[];
  statistics: {
    totalLines: number;
    parsedLines: number;
    format: string;
  };
}

// ====== EXPORT ======

export interface ExportEntry {
  entryDate: string;
  journalCode: string;
  entryNumber: string;
  accountCode: string;
  accountLabel: string;
  debit: number;
  credit: number;
  description: string;
  documentRef?: string;
}

export interface ExportConfig {
  format: string;
  dataType?: DataType;
  fiscalYearId?: string;
  encoding?: FileEncoding;
  separator?: FieldSeparator;
  includeHeader?: boolean;
  siren?: string;
  accountingFramework?: AccountingFramework;
}

export interface ExportFiscalYearParams {
  fileFormat: FileFormat;
  accountingStandard: AccountingStandard;
  dataType: DataType;
  startDate?: string;
  endDate?: string;
  encoding?: string;
  separator?: FieldSeparator;
  compress?: boolean;
  currency?: string;
  accountingFramework?: AccountingFramework;
}

export interface ExportFecParams {
  siren: string;
  closingDate: string;
}

// ====== TEMPLATES ======

export interface TemplatesResponse {
  success: boolean;
  templates: string[];
  description: Record<string, string>;
}

// ====== LABELS & HELPERS ======

export const ACCOUNTING_STANDARD_LABELS: Record<AccountingStandard, string> = {
  fec: 'FEC (Fichier des Écritures Comptables)',
  saf_t: 'SAF-T (Standard Audit File)',
  xbrl: 'XBRL (eXtensible Business Reporting)',
  syscohada: 'SYSCOHADA',
  ohada_revise: 'OHADA Révisé (2017+)',
  generic: 'Générique',
  pennylane: 'Pennylane',
  csv_standard: 'CSV Standard Comptable'
};

export const FILE_FORMAT_LABELS: Record<FileFormat, string> = {
  csv: 'CSV',
  txt: 'TXT',
  xlsx: 'Excel (XLSX)',
  xls: 'Excel (XLS)',
  xml: 'XML',
  json: 'JSON',
  pdf: 'PDF'
};

export const DATA_TYPE_LABELS: Record<DataType, string> = {
  fiscal_year: 'Exercice comptable',
  journal_entries: 'Écritures comptables',
  chart_of_accounts: 'Plan comptable',
  trial_balance: 'Balance des comptes',
  general_ledger: 'Grand livre',
  invoices: 'Factures',
  third_parties: 'Tiers'
};

export const OPERATION_STATUS_LABELS: Record<OperationStatus, string> = {
  pending: 'En attente',
  downloading: 'Téléchargement',
  processing: 'Traitement',
  parsing: 'Analyse',
  validating: 'Validation',
  importing: 'Importation',
  completed: 'Terminé',
  failed: 'Échoué',
  partial: 'Partiel',
  cancelled: 'Annulé'
};

/** Couleurs CSS pour chaque statut d'opération */
export const OPERATION_STATUS_COLORS: Record<OperationStatus, { bg: string; text: string; dot: string }> = {
  pending:     { bg: 'bg-gray-100 dark:bg-gray-800',     text: 'text-gray-600 dark:text-gray-400',     dot: 'bg-gray-400' },
  downloading: { bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400',     dot: 'bg-blue-500' },
  processing:  { bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400',     dot: 'bg-blue-500' },
  parsing:     { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-400', dot: 'bg-indigo-500' },
  validating:  { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400',   dot: 'bg-amber-500' },
  importing:   { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', dot: 'bg-purple-500' },
  completed:   { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400',   dot: 'bg-green-500' },
  failed:      { bg: 'bg-red-100 dark:bg-red-900/30',     text: 'text-red-700 dark:text-red-400',       dot: 'bg-red-500' },
  partial:     { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', dot: 'bg-orange-500' },
  cancelled:   { bg: 'bg-gray-100 dark:bg-gray-800',     text: 'text-gray-600 dark:text-gray-400',     dot: 'bg-gray-400' }
};
